import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { MessageService } from '../../../../../../message/message.service';
import { IdentityVerificationFacade } from '../../../../domain/+state/identity-verification.facade';
import { IdentityVerificationStatus, IdVerseFailedAction, IdVerseStateData } from '../../../../domain/models/identity-verification.model';
import { IdVersePollingService } from '../../../../infrastructure/id-verse-polling.service';
import {
  ID_VERSE_DESKTOP_POLLING_INTERVAL_MS,
  ID_VERSE_DESKTOP_POLLING_TIMEOUT_MS,
} from '../models/id-verse-content.data';
import { IdVerseContentConfig, IdVerseFlowType } from '../models/id-verse-content.model';
import { IdVerseContentStateService } from './id-verse-content-state.service';

@Injectable()
export class IdVerseDesktopFlowService {
  private pollingSubscription: Subscription;

  constructor(
    private readonly messageService: MessageService,
    private readonly identityVerificationFacade: IdentityVerificationFacade,
    private readonly idVersePollingService: IdVersePollingService,
    private readonly stateService: IdVerseContentStateService,
  ) { }

  handlePendingInitial(config: IdVerseContentConfig): void {
    this.handlePendingFlow(config, false, true);
  }

  handlePendingContinue(stateData: IdVerseStateData, customerId: number, config: IdVerseContentConfig): void {
    if (!stateData) {
      this.messageService.errorDefault('Unable to get identity verification data.');
      return;
    }

    this.stateService.update({ verificationUrl: stateData.verificationUrl });

    switch (stateData.status) {
      case IdentityVerificationStatus.None:
        this.startNewVerification(customerId, config);
        break;

      case IdentityVerificationStatus.Pending:
        this.handlePendingFlow(config, true);
        break;

      case IdentityVerificationStatus.Syncing:
        this.handleSyncingStatus(false);
        break;

      case IdentityVerificationStatus.Success:
        this.handleSuccessStatus();
        break;

      case IdentityVerificationStatus.Failed:
        this.handleFailedStatus(stateData.onFailedAction);
        break;

      default:
        this.messageService.errorDefault('Unknown identity verification status.');
        break;
    }
  }

  handleSyncingStatus(shouldRestartPolling: boolean = true): void {
    this.messageService.info('We\'re currently awaiting the results of your verification.');
    this.updateFlowType(IdVerseFlowType.StandardPendingFlow);

    if (shouldRestartPolling) {
      this.restartPolling();
    }
  }

  handleTryAgain(): void {
    this.updateFlowType(IdVerseFlowType.StandardPendingFlow);
    this.restartPolling();
    this.showQrScanMessage();
  }

  stopPolling(): void {
    if (!this.pollingSubscription) {
      return;
    }

    this.pollingSubscription.unsubscribe();
    this.pollingSubscription = null;
  }

  private handlePendingFlow(config: IdVerseContentConfig, showPendingMessage: boolean, shouldRestartPolling: boolean = false): void {
    const flowType = this.resolvePendingFlowType(config);
    this.updateFlowType(flowType, flowType !== IdVerseFlowType.MobileAppPromo);

    if (showPendingMessage && flowType !== IdVerseFlowType.MobileAppPromo) {
      this.messageService.warning('Verification still pending. Scan the QR code using your smartphone camera to continue or complete your ID verification.');
    }

    if (shouldRestartPolling) {
      this.restartPolling();
    }
  }

  private showQrScanMessage(): void {
    this.messageService.info('Scan the QR code using your smartphone camera to complete your ID verification.');
  }

  private startNewVerification(customerId: number, config: IdVerseContentConfig): void {
    this.identityVerificationFacade.storeVerification(customerId).subscribe((response) => {
      if (!response?.verificationUrl) {
        this.messageService.errorDefault('Unable to start new verification. Please try again later.');
        return;
      }

      this.stateService.update({ verificationUrl: response.verificationUrl });
      this.handlePendingInitial(config);
      this.showQrScanMessage();
    });
  }

  private resolvePendingFlowType(config: IdVerseContentConfig): IdVerseFlowType {
    if (config?.showMobileAppPromo && !this.stateService.getSkipPromo()) {
      return IdVerseFlowType.MobileAppPromo;
    }

    return IdVerseFlowType.StandardPendingFlow;
  }

  private restartPolling(): void {
    this.stopPolling();
    this.pollingSubscription = this.idVersePollingService.startPolling(
      ID_VERSE_DESKTOP_POLLING_INTERVAL_MS,
      ID_VERSE_DESKTOP_POLLING_TIMEOUT_MS,
    )
      .subscribe((stateData: IdVerseStateData) => this.handlePollingState(stateData));
  }

  private handlePollingState(stateData: IdVerseStateData): void {
    if (!stateData) {
      return;
    }

    switch (stateData.status) {
      case IdentityVerificationStatus.Success:
        this.handleSuccessStatus();
        break;

      case IdentityVerificationStatus.Failed:
        this.handleFailedStatus(stateData.onFailedAction);
        break;
    }
  }

  private handleSuccessStatus(): void {
    this.stopPolling();
    this.updateFlowType(IdVerseFlowType.Success);
  }

  private handleFailedStatus(onFailedAction: IdVerseFailedAction): void {
    this.stopPolling();
    this.updateFlowType(IdVerseFlowType.Failed, true, onFailedAction);
  }

  private updateFlowType(flowType: IdVerseFlowType, skipMobileAppPromo: boolean = true, failedAction?: IdVerseFailedAction): void {
    this.stateService.update({
      currentFlowType: flowType,
      skipMobileAppPromo,
      failedAction: failedAction || null,
    });
  }
}